// Component: TradeEconomicsPage.jsx
// Purpose: Superuser, append-only admin for the trade-economics schedule (144-6 R4): statutory exchange
//          charges (one atomic revision per key + date) and per-broker commissions, with key history,
//          circular / source links, unverified badges, audit fields and the live schedule digest.
//          Gateway-relative calls only (services.tradeEconomics); there is no edit or delete anywhere.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiClock } from "react-icons/fi";

import { Badge, Button, PageHeader, Segmented, Tabs } from "../../../components/ds";
import { useAuth } from "../../../contexts/AuthContext";
import { services } from "../../../services/api";

/*>>> VOCABULARY (closed lists: R1-T5 / R2-T1; the server validates and stays the authority) <<<*/

const EXCHANGES = ["NSE", "BSE", "MCX"];
const SEGMENTS = ["EQUITY", "FUTURE", "OPTION", "CURRENCY_FUTURE", "CURRENCY_OPTION"];
const PRODUCTS = ["INTRADAY", "DELIVERY", "CARRYFORWARD"];
const SIDES = ["buy", "sell", "both"];
const BASES = ["turnover", "premium", "brokerage_plus_fees", "per_order", "per_lot"];
//! A rate on these bases is a DECIMAL FRACTION on the wire; the UI shows and accepts PERCENT.
const FRACTION_BASES = new Set(["turnover", "premium", "brokerage_plus_fees"]);
const VERIFICATIONS = ["unverified", "verified"];
const BROKERAGE_MODELS = ["flat_per_order", "pct_of_turnover", "lower_of_flat_or_pct"];
//* executable brokers resolve for real orders; reference brokers are history only (R2-T5).

const COMPONENT_DEFAULTS = {
  stt_ctt: { side: "sell", basis: "turnover", mode: "rate" },
  exchange_txn: { side: "both", basis: "turnover", mode: "rate" },
  sebi: { side: "both", basis: "turnover", mode: "rate" },
  stamp: { side: "buy", basis: "turnover", mode: "rate" },
  gst: { side: "both", basis: "brokerage_plus_fees", mode: "rate" },
  clearing: { side: "both", basis: "turnover", mode: "omit" },
  dp_charges: { side: "sell", basis: "per_order", mode: "omit" },
};
const COMPONENT_NAMES = Object.keys(COMPONENT_DEFAULTS);

/*>>> PURE HELPERS <<<*/

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Asia/Kolkata",
  timeZoneName: "short",
});

const istDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

//--> effective_from is a DATE in IST (D-G1-31): today is read on the exchange calendar, not the browser's.
export function todayIST() {
  return istDateFormatter.format(new Date());
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : dateTimeFormatter.format(parsed);
}

const NUMBER_TEXT = /^\d*\.?\d+$|^\d+\.$/;

//! The ONE percent -> fraction conversion (string decimal shift: 0.03 -> 0.0003 with no float error).
//  Returns null for text that is not a plain non-negative decimal.
export function percentToFraction(text) {
  const raw = String(text ?? "").trim();
  if (!NUMBER_TEXT.test(raw)) return null;
  const [intPart = "", fracPart = ""] = raw.split(".");
  const digits = `${intPart}${fracPart}`;
  const point = intPart.length - 2; // two places left
  const shifted = point <= 0
    ? `0.${"0".repeat(-point)}${digits}`
    : `${digits.slice(0, point)}.${digits.slice(point)}`;
  return trimDecimal(shifted);
}

//! Display twin of percentToFraction: fraction text (0.0003) -> percent text (0.03).
export function fractionToPercent(text) {
  if (text === null || text === undefined || text === "") return "";
  const raw = String(text).trim();
  if (!NUMBER_TEXT.test(raw)) return raw;
  const [intPart = "", fracPart = ""] = raw.split(".");
  const digits = `${intPart}${fracPart}`;
  const point = intPart.length + 2; // two places right
  const shifted = point >= digits.length
    ? digits.padEnd(point, "0")
    : `${digits.slice(0, point)}.${digits.slice(point)}`;
  return trimDecimal(shifted);
}

function trimDecimal(text) {
  const [whole = "0", frac = ""] = text.split(".");
  const intText = whole.replace(/^0+(?=\d)/, "") || "0";
  const fracText = frac.replace(/0+$/, "");
  return fracText ? `${intText}.${fracText}` : intText;
}

function unwrap(response) {
  const body = response?.data;
  const data = body && typeof body === "object" && "data" in body ? body.data : body;
  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    digest: data?.digest ?? null,
  };
}

//--> Surface the server's own words: FastAPI 422 arrays name the field in `loc`; the schedule service
//    answers {code, message, field}.
export function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail ?? error?.response?.data?.error;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const loc = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== "body").join(".") : "";
        return loc ? `${loc}: ${item?.msg}` : item?.msg;
      })
      .filter(Boolean)
      .join("; ") || fallback;
  }
  if (detail && typeof detail === "object") {
    const field = detail.field ? `${detail.field}: ` : "";
    return `${field}${detail.message || fallback}`;
  }
  if (typeof detail === "string") return detail;
  return error?.message || fallback;
}

function groupByKey(rows, keyOf) {
  const groups = new Map();
  for (const row of rows) {
    const key = keyOf(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  return [...groups.entries()];
}

//--> in-force = greatest effective_from <= today (IST) within a key (D-G1-38).
function inForceDate(rows, today) {
  const dates = rows.map((row) => row.effective_from).filter((date) => date && date <= today).sort();
  return dates.length ? dates[dates.length - 1] : null;
}

function fieldNumber(text) {
  const raw = String(text ?? "").trim();
  if (!NUMBER_TEXT.test(raw)) return null;
  return Number(raw);
}

//! Edge conversion for a statutory rate field: percent bases -> fraction exactly once; amounts as-is.
function toWireRate(text, basis) {
  if (FRACTION_BASES.has(basis)) {
    const fraction = percentToFraction(text);
    return fraction === null ? null : Number(fraction);
  }
  return fieldNumber(text);
}

/*>>> SMALL PRESENTATIONAL PIECES <<<*/

function VerificationBadge({ status }) {
  if (status === "unverified") return <Badge variant="warn" size="sm">unverified</Badge>;
  if (status === "verified") return <Badge variant="profit" size="sm">verified</Badge>;
  return null;
}

function SourceLink({ id, url }) {
  if (!url) return <span className="text-fg-3">{id || "—"}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-accent underline">
      {id || url}
    </a>
  );
}

function Field({ label, htmlFor, children, hint }) {
  return (
    <div className="flex min-w-[140px] flex-col gap-1">
      <label htmlFor={htmlFor} className="text-[10px] font-bold uppercase tracking-wide text-fg-2">
        {label}
      </label>
      {children}
      {hint && <span className="text-[10px] text-fg-3">{hint}</span>}
    </div>
  );
}

function StatePanel({ icon, title, message }) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--divider)] bg-bg-2 px-6 py-12 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-bg-3 text-fg-3">{icon}</div>
      <p className="font-semibold text-fg-1">{title}</p>
      {message && <p className="mt-1 text-sm text-fg-3">{message}</p>}
    </div>
  );
}

function AccessPanel() {
  return (
    <div className="rounded-xl border border-loss/20 bg-loss/5 p-5" role="alert">
      <div className="flex items-start gap-3">
        <FiAlertCircle className="mt-0.5 h-5 w-5 flex-none text-loss" />
        <div>
          <p className="font-semibold text-loss">Admin Access Required</p>
          <p className="mt-1 text-sm text-fg-2">Trade economics access is limited to superusers.</p>
        </div>
      </div>
    </div>
  );
}

function FormMessage({ error, notice }) {
  if (error) return <p role="alert" className="mt-2 text-sm text-loss">{error}</p>;
  if (notice) return <p role="status" className="mt-2 text-sm text-profit">{notice}</p>;
  return null;
}

const INPUT = "ds-input w-full";

/*>>> STATUTORY TAB <<<*/

function statutoryRateText(row) {
  if (row.rate === null || row.rate === undefined) return `Disabled: ${row.disabled_reason || "no reason recorded"}`;
  if (FRACTION_BASES.has(row.basis)) return `${fractionToPercent(row.rate)} %`;
  return `₹${row.rate}`;
}

function StatutoryHistory({ rows, today }) {
  if (rows.length === 0) {
    return <StatePanel icon={<FiClock />} title="No statutory rows" message="No rows match this filter." />;
  }
  const keyed = groupByKey(rows, (row) => `${row.exchange} / ${row.segment} / ${row.product || "ALL PRODUCTS"}`);
  return (
    <div className="space-y-4">
      {keyed.map(([key, keyRows]) => {
        const live = inForceDate(keyRows, today);
        return (
          <section key={key} aria-label={`Statutory ${key}`} className="rounded-xl border border-[var(--divider)] bg-bg-2">
            <h3 className="border-b border-[var(--divider)] px-4 py-2 text-sm font-bold text-fg-1">{key}</h3>
            <div className="overflow-x-auto">
              <table className="ds-table dense w-full">
                <thead>
                  <tr>
                    <th>Effective from (IST)</th>
                    <th>Component</th>
                    <th>Side</th>
                    <th>Basis</th>
                    <th>Rate</th>
                    <th>Circular</th>
                    <th>Status</th>
                    <th>Created by</th>
                    <th>Created at</th>
                  </tr>
                </thead>
                <tbody>
                  {keyRows.map((row) => (
                    <tr key={row.id || `${key}-${row.component}-${row.effective_from}`}>
                      <td>
                        {row.effective_from}
                        {row.effective_from === live && <Badge variant="info" size="sm" className="ml-2">In force</Badge>}
                      </td>
                      <td>{row.component}</td>
                      <td>{row.side}</td>
                      <td>{row.basis}</td>
                      <td>
                        {statutoryRateText(row)}
                        {row.ipft_included_rate != null && (
                          <span className="ml-1 text-[10px] text-fg-3">
                            (incl. IPFT {fractionToPercent(row.ipft_included_rate)} %)
                          </span>
                        )}
                      </td>
                      <td><SourceLink id={row.circular_id} url={row.circular_url} /></td>
                      <td><VerificationBadge status={row.verification_status} /></td>
                      <td>{row.created_by ?? "—"}</td>
                      <td>{formatDateTime(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function blankComponent(name) {
  const d = COMPONENT_DEFAULTS[name];
  return {
    mode: d.mode,
    side: d.side,
    basis: d.basis,
    rate: "",
    ipft: "",
    circular_id: "",
    circular_url: "",
    source_note: "",
    disabled_reason: "",
    verification_status: "unverified",
  };
}

function blankStatutoryForm() {
  return {
    exchange: "MCX",
    segment: "FUTURE",
    product: "",
    effective_from: todayIST(),
    components: Object.fromEntries(COMPONENT_NAMES.map((name) => [name, blankComponent(name)])),
  };
}

function StatutoryForm({ rows, onAppended }) {
  const [form, setForm] = useState(blankStatutoryForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const setTop = (patch) => setForm((current) => ({ ...current, ...patch }));
  const setComp = (name, patch) => setForm((current) => ({
    ...current,
    components: { ...current.components, [name]: { ...current.components[name], ...patch } },
  }));

  //--> Convenience only: copy the key's latest revision into the form (rates shown as percent) so an
  //    omitted component cannot be an accident. It is a form fill, never a write.
  const prefill = () => {
    const keyRows = rows.filter((row) => (
      row.exchange === form.exchange && row.segment === form.segment && (row.product || "") === form.product
    ));
    const latest = keyRows.map((row) => row.effective_from).sort().pop();
    if (!latest) {
      setError("No existing revision for this key to prefill from.");
      return;
    }
    const components = Object.fromEntries(COMPONENT_NAMES.map((name) => [name, blankComponent(name)]));
    for (const row of keyRows.filter((candidate) => candidate.effective_from === latest)) {
      if (!components[row.component]) continue;
      const disabled = row.rate === null || row.rate === undefined;
      components[row.component] = {
        mode: disabled ? "disabled" : "rate",
        side: row.side,
        basis: row.basis,
        rate: disabled ? "" : (FRACTION_BASES.has(row.basis) ? fractionToPercent(row.rate) : String(row.rate)),
        ipft: row.ipft_included_rate != null ? fractionToPercent(row.ipft_included_rate) : "",
        circular_id: row.circular_id || "",
        circular_url: row.circular_url || "",
        source_note: row.source_note || "",
        disabled_reason: row.disabled_reason || "",
        verification_status: row.verification_status || "unverified",
      };
    }
    setError(null);
    setForm((current) => ({ ...current, components }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    const body = {
      exchange: form.exchange,
      segment: form.segment,
      product: form.product || null,
      effective_from: form.effective_from,
      components: [],
    };
    for (const name of COMPONENT_NAMES) {
      const c = form.components[name];
      if (c.mode === "omit") continue;
      if (c.mode === "disabled") {
        body.components.push({
          component: name,
          side: c.side,
          basis: c.basis,
          rate: null,
          disabled_reason: c.disabled_reason,
          circular_id: c.circular_id || null,
          circular_url: c.circular_url || null,
          source_note: c.source_note,
          verification_status: c.verification_status,
        });
        continue;
      }
      const rate = toWireRate(c.rate, c.basis);
      if (rate === null) {
        setError(`${name}.rate: enter a number${FRACTION_BASES.has(c.basis) ? " in %" : ""}`);
        return;
      }
      const entry = {
        component: name,
        side: c.side,
        basis: c.basis,
        rate,
        circular_id: c.circular_id,
        circular_url: c.circular_url,
        source_note: c.source_note,
        verification_status: c.verification_status,
      };
      if (name === "exchange_txn" && form.exchange !== "MCX" && String(c.ipft).trim() !== "") {
        const ipft = percentToFraction(c.ipft);
        if (ipft === null) {
          setError("exchange_txn.ipft_included_rate: enter a number in %");
          return;
        }
        entry.ipft_included_rate = Number(ipft);
      }
      body.components.push(entry);
    }
    setBusy(true);
    try {
      await services.tradeEconomics.appendStatutory(body);
      setNotice("Revision appended.");
      await onAppended();
    } catch (err) {
      setError(errorMessage(err, "Failed to append the statutory revision."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Add statutory revision" className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
      <h3 className="mb-1 text-sm font-bold text-fg-1">Add statutory revision</h3>
      <p className="mb-3 text-xs text-fg-3">
        One revision carries every component for the key and date. A component present in the previous
        revision must be repeated or disabled with a reason. Rates are entered in % (amount bases in ₹).
      </p>
      <div className="mb-4 flex flex-wrap gap-3">
        <Field label="Exchange" htmlFor="st-exchange">
          <select id="st-exchange" className={INPUT} value={form.exchange} onChange={(e) => setTop({ exchange: e.target.value })}>
            {EXCHANGES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Segment" htmlFor="st-segment">
          <select id="st-segment" className={INPUT} value={form.segment} onChange={(e) => setTop({ segment: e.target.value })}>
            {SEGMENTS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Product" htmlFor="st-product">
          <select id="st-product" className={INPUT} value={form.product} onChange={(e) => setTop({ product: e.target.value })}>
            <option value="">All products</option>
            {PRODUCTS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Effective from (IST)" htmlFor="st-effective" hint="Not earlier than today">
          <input id="st-effective" type="date" className={INPUT} min={todayIST()} value={form.effective_from}
            onChange={(e) => setTop({ effective_from: e.target.value })} required />
        </Field>
        <div className="flex items-end">
          <Button variant="ghost" size="sm" onClick={prefill}>Prefill from in-force set</Button>
        </div>
      </div>

      <div className="space-y-3">
        {COMPONENT_NAMES.map((name) => {
          const c = form.components[name];
          const percent = FRACTION_BASES.has(c.basis);
          return (
            <fieldset key={name} className="rounded-lg border border-[var(--divider)] p-3">
              <legend className="px-1 text-xs font-bold uppercase text-fg-2">{name}</legend>
              <div className="flex flex-wrap gap-3">
                <Field label="Treatment" htmlFor={`st-${name}-mode`}>
                  <select id={`st-${name}-mode`} className={INPUT} value={c.mode} onChange={(e) => setComp(name, { mode: e.target.value })}>
                    <option value="rate">Set rate</option>
                    <option value="disabled">Disable (reason)</option>
                    <option value="omit">Omit</option>
                  </select>
                </Field>
                {c.mode !== "omit" && (
                  <>
                    <Field label="Side" htmlFor={`st-${name}-side`}>
                      <select id={`st-${name}-side`} className={INPUT} value={c.side} onChange={(e) => setComp(name, { side: e.target.value })}>
                        {SIDES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </Field>
                    <Field label="Basis" htmlFor={`st-${name}-basis`}>
                      <select id={`st-${name}-basis`} className={INPUT} value={c.basis} disabled={name === "gst"}
                        onChange={(e) => setComp(name, { basis: e.target.value })}>
                        {BASES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </Field>
                  </>
                )}
                {c.mode === "rate" && (
                  <>
                    <Field label={`Rate (${percent ? "%" : "₹"})`} htmlFor={`st-${name}-rate`}>
                      <input id={`st-${name}-rate`} inputMode="decimal" className={INPUT} value={c.rate}
                        onChange={(e) => setComp(name, { rate: e.target.value })} />
                    </Field>
                    {name === "exchange_txn" && form.exchange !== "MCX" && (
                      <Field label="IPFT included (%)" htmlFor={`st-${name}-ipft`} hint="Informational only">
                        <input id={`st-${name}-ipft`} inputMode="decimal" className={INPUT} value={c.ipft}
                          onChange={(e) => setComp(name, { ipft: e.target.value })} />
                      </Field>
                    )}
                    <Field label="Circular ID" htmlFor={`st-${name}-cid`}>
                      <input id={`st-${name}-cid`} className={INPUT} value={c.circular_id}
                        onChange={(e) => setComp(name, { circular_id: e.target.value })} />
                    </Field>
                    <Field label="Circular URL" htmlFor={`st-${name}-curl`}>
                      <input id={`st-${name}-curl`} className={INPUT} value={c.circular_url}
                        onChange={(e) => setComp(name, { circular_url: e.target.value })} />
                    </Field>
                  </>
                )}
                {c.mode === "disabled" && (
                  <Field label="Reason for disabling" htmlFor={`st-${name}-reason`}>
                    <input id={`st-${name}-reason`} className={INPUT} value={c.disabled_reason}
                      onChange={(e) => setComp(name, { disabled_reason: e.target.value })} />
                  </Field>
                )}
                {c.mode !== "omit" && (
                  <>
                    <Field label="Source note" htmlFor={`st-${name}-note`}>
                      <input id={`st-${name}-note`} className={INPUT} value={c.source_note}
                        onChange={(e) => setComp(name, { source_note: e.target.value })} />
                    </Field>
                    <Field label="Verification" htmlFor={`st-${name}-ver`}>
                      <select id={`st-${name}-ver`} className={INPUT} value={c.verification_status}
                        onChange={(e) => setComp(name, { verification_status: e.target.value })}>
                        {VERIFICATIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </Field>
                  </>
                )}
              </div>
            </fieldset>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" disabled={busy}>{busy ? "Appending…" : "Append statutory revision"}</Button>
      </div>
      <FormMessage error={error} notice={notice} />
    </form>
  );
}

/*>>> BROKER TAB <<<*/

function brokerageText(row) {
  const parts = [];
  if (row.flat_amount != null) parts.push(`₹${row.flat_amount}`);
  if (row.pct != null) parts.push(`${fractionToPercent(row.pct)} %`);
  return parts.length ? parts.join(" / ") : "—";
}

function BrokerHistory({ rows, today }) {
  if (rows.length === 0) {
    return <StatePanel icon={<FiClock />} title="No broker rows" message="No rows match this filter." />;
  }
  const keyed = groupByKey(rows, (row) => `${row.broker} / ${row.exchange} / ${row.segment} / ${row.product}`);
  return (
    <div className="space-y-4">
      {keyed.map(([key, keyRows]) => {
        const live = inForceDate(keyRows, today);
        return (
          <section key={key} aria-label={`Broker ${key}`} className="rounded-xl border border-[var(--divider)] bg-bg-2">
            <h3 className="border-b border-[var(--divider)] px-4 py-2 text-sm font-bold text-fg-1">{key}</h3>
            <div className="overflow-x-auto">
              <table className="ds-table dense w-full">
                <thead>
                  <tr>
                    <th>Effective from (IST)</th>
                    <th>Model</th>
                    <th>Brokerage</th>
                    <th>Cap</th>
                    <th>GST on brokerage</th>
                    <th>Extra fees</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Created by</th>
                    <th>Created at</th>
                  </tr>
                </thead>
                <tbody>
                  {keyRows.map((row) => (
                    <tr key={row.id || `${key}-${row.effective_from}`}>
                      <td>
                        {row.effective_from}
                        {row.effective_from === live && <Badge variant="info" size="sm" className="ml-2">In force</Badge>}
                      </td>
                      <td>{row.brokerage_model}</td>
                      <td>{brokerageText(row)}</td>
                      <td>{row.cap_amount != null ? `₹${row.cap_amount}` : "—"}</td>
                      <td>{row.gst_on_brokerage ? "Yes" : "No"}</td>
                      <td>
                        {(row.extra_fees || []).length === 0
                          ? "—"
                          : row.extra_fees.map((fee) => `${fee.name} ₹${fee.amount}${fee.gst_on_fee ? " + GST" : ""}`).join(", ")}
                      </td>
                      <td><SourceLink id={row.source_url} url={row.source_url} /></td>
                      <td>
                        <VerificationBadge status={row.verification_status} />
                        {row.is_reference && <Badge variant="neutral" size="sm" className="ml-1">reference</Badge>}
                      </td>
                      <td>{row.created_by ?? "—"}</td>
                      <td>{formatDateTime(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function blankBrokerForm() {
  return {
    broker: "",
    is_reference: false,
    exchange: "MCX",
    segment: "FUTURE",
    product: "INTRADAY",
    brokerage_model: "flat_per_order",
    flat_amount: "",
    pct: "",
    cap_amount: "",
    gst_on_brokerage: true,
    extra_fees: [],
    effective_from: todayIST(),
    source_url: "",
    source_note: "",
    verification_status: "unverified",
  };
}

//--> R2-F3: broker keys and their executable-vs-reference class are DATA (the history rows), never a
//    hard-coded list; a brand-new key is free text and the server validates it (BrokerRowIn).
function knownBrokerClasses(rows) {
  const known = new Map();
  for (const row of rows || []) known.set(row.broker, Boolean(row.is_reference));
  return known;
}

function BrokerForm({ onAppended, rows }) {
  const [form, setForm] = useState(blankBrokerForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const known = useMemo(() => knownBrokerClasses(rows), [rows]);
  const brokerKey = form.broker.trim().toLowerCase();
  const knownClass = known.has(brokerKey) ? known.get(brokerKey) : null;
  const isReference = knownClass === null ? form.is_reference : knownClass;

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const setFee = (index, patch) => setForm((current) => ({
    ...current,
    extra_fees: current.extra_fees.map((fee, i) => (i === index ? { ...fee, ...patch } : fee)),
  }));
  const needsFlat = form.brokerage_model === "flat_per_order" || form.brokerage_model === "lower_of_flat_or_pct";
  const needsPct = form.brokerage_model === "pct_of_turnover" || form.brokerage_model === "lower_of_flat_or_pct";

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    const body = {
      broker: brokerKey,
      is_reference: isReference,
      exchange: form.exchange,
      segment: form.segment,
      product: form.product,
      brokerage_model: form.brokerage_model,
      gst_on_brokerage: form.gst_on_brokerage,
      effective_from: form.effective_from,
      source_url: form.source_url,
      source_note: form.source_note,
      verification_status: form.verification_status,
      extra_fees: [],
    };
    if (needsFlat) {
      const flat = fieldNumber(form.flat_amount);
      if (flat === null) {
        setError("flat_amount: enter a number in ₹");
        return;
      }
      body.flat_amount = flat;
    }
    if (needsPct) {
      //! percent -> fraction exactly once, at the edge (D-G1-35)
      const pct = percentToFraction(form.pct);
      if (pct === null) {
        setError("pct: enter a number in %");
        return;
      }
      body.pct = Number(pct);
    }
    if (String(form.cap_amount).trim() !== "") {
      const cap = fieldNumber(form.cap_amount);
      if (cap === null) {
        setError("cap_amount: enter a number in ₹");
        return;
      }
      body.cap_amount = cap;
    }
    for (const [index, fee] of form.extra_fees.entries()) {
      const amount = fieldNumber(fee.amount);
      if (!fee.name.trim() || amount === null) {
        setError(`extra_fees.${index}: a name and a ₹ amount are required`);
        return;
      }
      body.extra_fees.push({ name: fee.name.trim(), amount, gst_on_fee: fee.gst_on_fee });
    }
    setBusy(true);
    try {
      await services.tradeEconomics.appendBroker(body);
      setNotice("Broker row appended.");
      await onAppended();
    } catch (err) {
      setError(errorMessage(err, "Failed to append the broker row."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Add broker row" className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
      <h3 className="mb-1 text-sm font-bold text-fg-1">Add broker commission row</h3>
      <p className="mb-3 text-xs text-fg-3">
        Verifying a row means appending a later-dated copy with the primary-source URL and status
        &quot;verified&quot;. Percentages are entered in %, amounts in ₹.
      </p>
      <div className="flex flex-wrap gap-3">
        <Field label="Broker" htmlFor="br-broker">
          <input id="br-broker" list="br-broker-keys" className={INPUT} value={form.broker} required
            onChange={(e) => set({ broker: e.target.value })} autoComplete="off" />
          <datalist id="br-broker-keys">
            {[...known.keys()].map((v) => <option key={v} value={v} />)}
          </datalist>
        </Field>
        <Field label="Reference only" htmlFor="br-reference"
          hint={knownClass === null ? "New key: tick for a priced-for-comparison broker that cannot execute" : "Taken from the existing rows"}>
          <input id="br-reference" type="checkbox" checked={isReference} disabled={knownClass !== null}
            onChange={(e) => set({ is_reference: e.target.checked })} />
        </Field>
        <Field label="Exchange" htmlFor="br-exchange">
          <select id="br-exchange" className={INPUT} value={form.exchange} onChange={(e) => set({ exchange: e.target.value })}>
            {EXCHANGES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Segment" htmlFor="br-segment">
          <select id="br-segment" className={INPUT} value={form.segment} onChange={(e) => set({ segment: e.target.value })}>
            {SEGMENTS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Product" htmlFor="br-product">
          <select id="br-product" className={INPUT} value={form.product} onChange={(e) => set({ product: e.target.value })}>
            {PRODUCTS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Effective from (IST)" htmlFor="br-effective" hint="Not earlier than today">
          <input id="br-effective" type="date" className={INPUT} min={todayIST()} value={form.effective_from}
            onChange={(e) => set({ effective_from: e.target.value })} required />
        </Field>
        <Field label="Brokerage model" htmlFor="br-model">
          <select id="br-model" className={INPUT} value={form.brokerage_model} onChange={(e) => set({ brokerage_model: e.target.value })}>
            {BROKERAGE_MODELS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        {needsFlat && (
          <Field label="Flat amount (₹)" htmlFor="br-flat">
            <input id="br-flat" inputMode="decimal" className={INPUT} value={form.flat_amount} onChange={(e) => set({ flat_amount: e.target.value })} />
          </Field>
        )}
        {needsPct && (
          <Field label="Percent of turnover (%)" htmlFor="br-pct">
            <input id="br-pct" inputMode="decimal" className={INPUT} value={form.pct} onChange={(e) => set({ pct: e.target.value })} />
          </Field>
        )}
        <Field label="Cap (₹, optional)" htmlFor="br-cap">
          <input id="br-cap" inputMode="decimal" className={INPUT} value={form.cap_amount} onChange={(e) => set({ cap_amount: e.target.value })} />
        </Field>
        <Field label="Source URL" htmlFor="br-source">
          <input id="br-source" className={INPUT} value={form.source_url} onChange={(e) => set({ source_url: e.target.value })} />
        </Field>
        <Field label="Source note" htmlFor="br-note">
          <input id="br-note" className={INPUT} value={form.source_note} onChange={(e) => set({ source_note: e.target.value })} />
        </Field>
        <Field label="Verification" htmlFor="br-ver">
          <select id="br-ver" className={INPUT} value={form.verification_status} onChange={(e) => set({ verification_status: e.target.value })}>
            {VERIFICATIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <label className="flex items-end gap-2 pb-2 text-xs font-semibold text-fg-2">
          <input type="checkbox" checked={form.gst_on_brokerage} onChange={(e) => set({ gst_on_brokerage: e.target.checked })} />
          GST on brokerage (18 %)
        </label>
      </div>

      <div className="mt-3">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-fg-2">Extra fee lines</p>
        {form.extra_fees.map((fee, index) => (
          <div key={index} className="mb-2 flex flex-wrap items-end gap-3">
            <Field label="Fee name" htmlFor={`br-fee-${index}-name`}>
              <input id={`br-fee-${index}-name`} className={INPUT} value={fee.name} onChange={(e) => setFee(index, { name: e.target.value })} />
            </Field>
            <Field label="Amount (₹)" htmlFor={`br-fee-${index}-amount`}>
              <input id={`br-fee-${index}-amount`} inputMode="decimal" className={INPUT} value={fee.amount}
                onChange={(e) => setFee(index, { amount: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 pb-2 text-xs font-semibold text-fg-2">
              <input type="checkbox" checked={fee.gst_on_fee} onChange={(e) => setFee(index, { gst_on_fee: e.target.checked })} />
              GST on fee
            </label>
            <Button variant="ghost" size="sm" aria-label={`Discard fee line ${index + 1}`}
              onClick={() => set({ extra_fees: form.extra_fees.filter((_, i) => i !== index) })}>
              ×
            </Button>
          </div>
        ))}
        <Button variant="ghost" size="sm"
          onClick={() => set({ extra_fees: [...form.extra_fees, { name: "", amount: "", gst_on_fee: false }] })}>
          Add fee line
        </Button>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" disabled={busy}>{busy ? "Appending…" : "Append broker row"}</Button>
      </div>
      <FormMessage error={error} notice={notice} />
    </form>
  );
}

/*>>> PAGE <<<*/

const TAB_STATUTORY = "statutory";
const TAB_BROKER = "broker";

function useScheduleList(loader, enabled) {
  const [state, setState] = useState({ rows: [], digest: null, loading: enabled, error: null });
  const epoch = useRef(0);

  const load = useCallback(({ showLoading = true } = {}) => {
    if (!enabled) return Promise.resolve();
    const mine = ++epoch.current;
    if (showLoading) setState((current) => ({ ...current, loading: true, error: null }));
    return loader()
      .then((response) => {
        if (epoch.current !== mine) return;
        const { rows, digest } = unwrap(response);
        setState({ rows, digest, loading: false, error: null });
      })
      .catch((error) => {
        if (epoch.current !== mine) return;
        const status = error?.response?.status ?? error?.status;
        setState((current) => ({ ...current, loading: false, error: status === 403 ? "forbidden" : "error" }));
      });
  }, [loader, enabled]);

  useEffect(() => {
    load();
    return () => { epoch.current += 1; };
  }, [load]);

  return [state, load];
}

const listStatutory = () => services.tradeEconomics.listStatutory();
const listBroker = () => services.tradeEconomics.listBroker();

export default function TradeEconomicsPage() {
  const { user } = useAuth();
  const isSuperuser = user?.is_superuser === true;
  const [tab, setTab] = useState(TAB_STATUTORY);
  const [exchange, setExchange] = useState("all");
  const [statutory, reloadStatutory] = useScheduleList(listStatutory, isSuperuser);
  const [broker, reloadBroker] = useScheduleList(listBroker, isSuperuser);
  const today = todayIST();

  if (!user) return null;

  const active = tab === TAB_STATUTORY ? statutory : broker;
  const digest = active.digest ?? statutory.digest ?? broker.digest;
  const byExchange = (rows) => (exchange === "all" ? rows : rows.filter((row) => row.exchange === exchange));

  return (
    <div className="min-h-screen bg-main-bg px-4 pb-10 pt-0 dark:bg-main-dark-bg md:px-6">
      <PageHeader
        title="Trade Economics"
        subtitle="Append-only exchange charges and broker commissions that price every backtest, paper and live fill"
        actions={
          isSuperuser ? (
            <Segmented
              aria-label="Exchange filter"
              value={exchange}
              onChange={setExchange}
              options={[{ value: "all", label: "All" }, ...EXCHANGES.map((value) => ({ value, label: value }))]}
            />
          ) : null
        }
      />

      {!isSuperuser ? (
        <AccessPanel />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-fg-2">
            <span className="font-bold uppercase tracking-wide">Schedule digest</span>
            <code data-testid="schedule-digest" className="rounded bg-bg-3 px-2 py-1 font-mono text-fg-1">
              {digest || "—"}
            </code>
          </div>

          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: TAB_STATUTORY, label: "Statutory schedule" },
              { value: TAB_BROKER, label: "Broker commissions" },
            ]}
          />

          <div className="mt-4 space-y-4">
            {tab === TAB_STATUTORY ? (
              <>
                {statutory.loading ? (
                  <StatePanel icon={<FiClock />} title="Loading statutory schedule…" />
                ) : statutory.error ? (
                  <StatePanel icon={<FiAlertCircle />} title="Failed to load the statutory schedule"
                    message={statutory.error === "forbidden" ? "Your session does not have gateway admin scope." : "The gateway could not load the schedule."} />
                ) : (
                  <StatutoryHistory rows={byExchange(statutory.rows)} today={today} />
                )}
                <StatutoryForm rows={statutory.rows} onAppended={() => reloadStatutory({ showLoading: false })} />
              </>
            ) : (
              <>
                {broker.loading ? (
                  <StatePanel icon={<FiClock />} title="Loading broker commissions…" />
                ) : broker.error ? (
                  <StatePanel icon={<FiAlertCircle />} title="Failed to load broker commissions"
                    message={broker.error === "forbidden" ? "Your session does not have gateway admin scope." : "The gateway could not load the schedule."} />
                ) : (
                  <BrokerHistory rows={byExchange(broker.rows)} today={today} />
                )}
                <BrokerForm rows={broker.rows} onAppended={() => reloadBroker({ showLoading: false })} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

